import React from 'react';
import { BackLink, DemoFooter, LenticMark } from '../../components/demo/DemoChrome';
import { GATED_ON, RecordScenarioRow, SOURCE_ID, observedBehaviour, recordScenarioSchedule, reconstruction } from '../../data/demo';
const CATEGORY_STYLE: Record<RecordScenarioRow['category'], string> = {
  'Handled and correctly resolved': 'border-brand/40 bg-brand-tint text-brand',
  'Clean case, verified proportionately': 'border-rule bg-paper text-muted',
  'Handled and appropriately escalated': 'border-brand/25 bg-white text-brand',
  'Over-checked, or resolved incorrectly': 'border-signal-amber/40 bg-white text-signal-amber',
  Missed: 'border-signal-red/40 bg-white text-signal-red'
};
const ACTOR_STYLE = {
  candidate: 'text-ink',
  assistant: 'text-brand',
  system: 'text-muted'
};
export function DecisionRecord() {
  return <div className="flex min-h-full w-full flex-col bg-paper" data-id="element-520" data-mp="3t9kprlpcozff">
      <div className="flex items-center justify-between px-8 pt-8" data-id="element-521" data-mp="135ozqu07r83">
        <LenticMark data-id="element-522" data-mp="2uijkki6tex8m" />
        <BackLink to="/demo/instrument" label="Back to the instrument" data-id="element-523" data-mp="2uiteuhyw7qk4" />
      </div>

      <main className="mx-auto w-full max-w-[1000px] flex-1 px-8 py-12" data-id="element-524" data-mp="2pjqc546dxwfb">
        <div className="rounded-sm border border-rule bg-white px-5 py-3 font-mono-num text-[11px] text-muted" data-id="element-525" data-mp="2plt9k99varu2">
          FIN-001 v0.1 · generated from {SOURCE_ID} · gated {GATED_ON} · reviewed by —
        </div>

        <p className="eyebrow mt-10 text-brand" data-id="element-526" data-mp="3d91ayg46dgqt">Decision record</p>
        <h1 className="mt-3 text-[30px] font-semibold tracking-tight text-ink" data-id="element-527" data-mp="3d89ez46o1h00">
          Participant LNT-0417
        </h1>
        <p className="mt-2 max-w-2xl text-[14px] leading-relaxed text-muted" data-id="element-528" data-mp="33j82hnzbxhqs">
          Meridian SAS Q2 2026 close · submitted in 47m 12s of 60m · 9 prompts to the assistant · 5
          of 7 source documents opened. No score is produced.
        </p>

        <section className="mt-12" data-id="element-529" data-mp="3w0an6x5byjm3">
          <p className="eyebrow text-muted" data-id="element-530" data-mp="3w1df98ee2s3l">Scenario schedule</p>
          <table className="mt-4 w-full border-collapse text-left" data-id="element-531" data-mp="3r80sz4x6mxe0">
            <thead data-id="element-532" data-mp="1paqmav8ryjqz">
              <tr className="border-b border-rule font-mono-num text-[10.5px] uppercase tracking-[0.1em] text-muted" data-id="element-533" data-mp="1pcl4w2wkpzrw">
                <th className="py-2.5 pr-4 font-medium" data-id="element-534" data-mp="1u9jy8j8ey2p1">Slot</th>
                <th className="py-2.5 pr-4 font-medium" data-id="element-535" data-mp="1fm8mag8bkgoe">Class</th>
                <th className="py-2.5 pr-4 font-medium" data-id="element-536" data-mp="1kkjvqpupcp63">Row</th>
                <th className="py-2.5 pr-4 font-medium" data-id="element-537" data-mp="1kiyyutkx0u52">What happened</th>
                <th className="py-2.5 font-medium" data-id="element-538" data-mp="16jpklzg9sa3q">Read as</th>
              </tr>
            </thead>
            <tbody data-id="element-539" data-mp="1anf6gh5k8dl2">
              {recordScenarioSchedule.map(r => <tr key={r.slotId} className="border-b border-rule/70 align-top" data-id="element-540" data-mp="wn4gdgc2r30y">
                  <td className="py-4 pr-4 font-mono-num text-[11.5px] text-muted" data-id="element-541" data-mp="11lo5fplysuxj">{r.slotId}</td>
                  <td className="py-4 pr-4 font-mono-num text-[10.5px] uppercase tracking-[0.08em] text-brand" data-id="element-542" data-mp="2r0m71c7s0g07">
                    {r.klass}
                  </td>
                  <td className="py-4 pr-4 font-mono-num text-[11.5px] text-muted" data-id="element-543" data-mp="2vz6z2ocvjtdk">{r.row}</td>
                  <td className="max-w-md py-4 pr-4 text-[13px] leading-relaxed text-ink" data-id="element-544" data-mp="kcv8ideswe7a">
                    {r.outcome}
                    <span className="mt-1 block font-mono-num text-[10.5px] text-muted" data-id="element-545" data-mp="fbfomood0dqh">
                      sources: {r.sources}
                    </span>
                  </td>
                  <td className="py-4" data-id="element-546" data-mp="pa57okrzs48b">
                    <span className={`inline-block max-w-[150px] rounded-sm border px-2 py-1 text-[11px] font-medium leading-snug ${CATEGORY_STYLE[r.category]}`} data-id="element-547" data-mp="132l59624ja1u">
                      {r.category}
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </section>

        <section className="mt-14" data-id="element-548" data-mp="3etfbk0cb99o2">
          <p className="eyebrow text-muted" data-id="element-549" data-mp="3eui0i2q2p21c">Reconstruction · 00:14:31</p>
          <p className="mt-2 max-w-2xl text-[13.5px] leading-relaxed text-muted" data-id="element-550" data-mp="39um6mwb3aqzi">
            One window, replayed in order. This is the sequence a reviewer reads to see how a
            conclusion was reached.
          </p>
          <ol className="mt-6 border-l border-rule" data-id="element-551" data-mp="25xjwdnqhdyu5">
            {reconstruction.map((e, i) => <li key={i} className="relative grid grid-cols-[92px_minmax(0,1fr)] gap-4 pb-5 pl-6" data-id="element-552" data-mp="2asfnmlnw8i67" data-mpi={i}>
                <span className="absolute left-0 top-1.5 h-1.5 w-1.5 -translate-x-[3.5px] rounded-full bg-rule" data-id="element-553" data-mp="2eu16qor0tsou" />
                <span className="font-mono-num text-[11.5px] text-muted" data-id="element-554" data-mp="1n4z5gbuce3sz">{e.at}</span>
                <span className={`text-[13.5px] leading-relaxed ${ACTOR_STYLE[e.actor]}`} data-id="element-555" data-mp="1n2v5u63h7qyi">
                  <span className="mr-2 font-mono-num text-[10.5px] uppercase tracking-wider text-muted/70" data-id="element-556" data-mp="1r4gc38li96zq">
                    {e.actor}
                  </span>
                  {e.text}
                </span>
              </li>)}
          </ol>
        </section>

        <section className="mt-14" data-id="element-557" data-mp="13fnllrnnjrci">
          <p className="eyebrow text-muted" data-id="element-558" data-mp="13em16wkqxsi4">Observed behaviour</p>
          <div className="mt-4 grid gap-5 sm:grid-cols-2" data-id="element-559" data-mp="vwxlyrun7aqe">
            {observedBehaviour.map(o => <div key={o.heading} className="rounded-sm border border-rule bg-white p-5" data-id="element-560" data-mp="qz5uu01tnsn9">
                <h2 className="text-[14px] font-semibold text-ink" data-id="element-561" data-mp="mv7trjsxs7rk">{o.heading}</h2>
                <p className="mt-2 text-[13px] leading-relaxed text-muted" data-id="element-562" data-mp="hwmw9s52k26t">{o.body}</p>
              </div>)}
          </div>
        </section>

        <section className="mt-14" data-id="element-563" data-mp="87v71rre7vzl">
          <p className="eyebrow text-muted" data-id="element-564" data-mp="87mvq4ax4yqi">Human review</p>
          <div className="mt-4 rounded-sm border border-dashed border-rule bg-white p-8" data-id="element-565" data-mp="38s021rurfoz">
            <div className="grid gap-6 sm:grid-cols-3" data-id="element-566" data-mp="36fk1u5nnfek">
              {['Reviewer', 'Date', 'Decision'].map(f => <div key={f} data-id="element-567" data-mp="3qd88dhszzbe5">
                  <p className="font-mono-num text-[10.5px] uppercase tracking-[0.1em] text-muted" data-id="element-568" data-mp="3qcgbm3nnz7ma">
                    {f}
                  </p>
                  <div className="mt-3 h-px w-full bg-rule" data-id="element-569" data-mp="3lgr85p8ix6kp" />
                </div>)}
            </div>
            <div className="mt-8" data-id="element-570" data-mp="3gpvrtpmhxl52">
              <p className="font-mono-num text-[10.5px] uppercase tracking-[0.1em] text-muted" data-id="element-571" data-mp="3gqx4tzn2ohpf">
                Reasoning
              </p>
              <div className="mt-3 space-y-5" data-id="element-572" data-mp="31upqvh29ccil">
                <div className="h-px w-full bg-rule" data-id="element-573" data-mp="36qwmafx125ma" />
                <div className="h-px w-full bg-rule" data-id="element-574" data-mp="36tj2f3xzhtg4" />
                <div className="h-px w-2/3 bg-rule" data-id="element-575" data-mp="3uibton3nvd4e" />
              </div>
            </div>
          </div>
          <p className="mt-8 text-center text-[15px] text-muted" data-id="element-576" data-mp="39o600hy7dto">
            The system described. A person decides.
          </p>
        </section>

        <div className="mt-12 flex justify-center" data-id="element-577" data-mp="3pqk86qzd3xl4">
          <button type="button" className="rounded-sm bg-brand px-7 py-3.5 text-[14px] font-semibold text-white transition-colors duration-150 ease-settle hover:bg-brand-dark" data-id="element-578" data-mp="3pp940q208nth">
            Book a walkthrough
          </button>
        </div>
      </main>

      <DemoFooter data-id="element-579" data-mp="26qujd4faxi97" />
    </div>;
}